import { useTranslation } from 'react-i18next'

import {
  isCampaignCalculationNumberOfClosedTradesType,
  isCampaignCalculationNumberOfProfitableTradesType,
  isCampaignCalculationPnlUsdType,
  isCampaignCalculationRoiPercentageType,
} from '@model/CampaignCalculationType'
import {
  ClientContestCampaignDetailDto,
  isCampaignBusinessRuleMinActiveTradingDaysPerCampaign,
  isCampaignBusinessRuleMinActiveTradingDaysPerMonth,
} from '@model/ClientContestCampaignDetailDto'
import { NameDto } from '@model/NameDto'

export const useCampaignContestValues = (campaign?: ClientContestCampaignDetailDto) => {
  const { t } = useTranslation()

  const calcId = campaign?.calculationType?.id
  const calcName = campaign?.calculationType?.name || ''

  const getCampaignContestPointsValue = () => {
    if (isCampaignCalculationRoiPercentageType(calcId)) return t('Campaigns.ROI')
    if (isCampaignCalculationNumberOfProfitableTradesType(calcId)) {
      return t('Campaigns.Number of profitable trades')
    }
    if (isCampaignCalculationNumberOfClosedTradesType(calcId)) {
      return t('Campaigns.Number of closed trades')
    }
    if (isCampaignCalculationPnlUsdType(calcId)) return t('Campaigns.PnL')
    return calcName || t('Campaigns.Points')
  }

  const getCampaignContestPointsCurrentValue = () => {
    if (isCampaignCalculationRoiPercentageType(calcId)) return t('Campaigns.Current ROI')
    if (isCampaignCalculationNumberOfProfitableTradesType(calcId)) {
      return t('Campaigns.Number of profitable trades')
    }
    if (isCampaignCalculationNumberOfClosedTradesType(calcId)) {
      return t('Campaigns.Number of closed trades')
    }
    if (isCampaignCalculationPnlUsdType(calcId)) return t('Campaigns.Current PnL')
    return calcName || t('Campaigns.Points')
  }

  const getCampaignContestPointsSymbolCurrent = () => {
    if (isCampaignCalculationRoiPercentageType(calcId)) return '%'
    if (isCampaignCalculationPnlUsdType(calcId)) return '$'
    return ''
  }

  const getCampaignContestPointsSymbol = () => {
    if (isCampaignCalculationRoiPercentageType(calcId)) return '%'
    if (isCampaignCalculationPnlUsdType(calcId)) return '$'
    return ''
  }

  const getCampaignContestPointsDescription = () => {
    if (isCampaignCalculationRoiPercentageType(calcId)) {
      return t(
        'Campaigns.Return on Investment (ROI) is the percentage of return you made on the amount(s) you deposited in the campaign account. Updates hourly'
      )
    }
    if (isCampaignCalculationNumberOfProfitableTradesType(calcId)) {
      return t('Campaigns.The total number of closed trades that ended with a profit')
    }
    if (isCampaignCalculationNumberOfClosedTradesType(calcId)) {
      return t(
        'Campaigns.The total number of trades that have been closed, including both profitable and losing trades'
      )
    }
    if (isCampaignCalculationPnlUsdType(calcId)) {
      return t(
        'Campaigns.PnL (Profit and Loss) shows the profit or loss from a traders open and closed positions in $'
      )
    }
    return ''
  }

  const getTradingDaysValue = (businessRule?: NameDto) => {
    if (isCampaignBusinessRuleMinActiveTradingDaysPerCampaign(businessRule)) {
      return t('Campaigns.Trading Days per campaign')
    }
    if (isCampaignBusinessRuleMinActiveTradingDaysPerMonth(businessRule)) {
      return t('Campaigns.Trading Days per month')
    }
    return t('Campaigns.Trading Days')
  }

  return {
    getCampaignContestPointsValue,
    getCampaignContestPointsCurrentValue,
    getTradingDaysValue,
    getCampaignContestPointsDescription,
    getCampaignContestPointsSymbolCurrent,
    getCampaignContestPointsSymbol,
  }
}
